import { PencilIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { extractStorageResources } from '@campai/lib-common/entities'
import { formatDateTime } from '@campai/lib-common/i18n'
import { Actor, ActorName, Markdown } from '@campai/lib-web/components'
import { cn } from '@campai/lib-web/components/new/lib'
import { Badge, Button, Card, CardHeader } from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { isOwnCommunityContent } from '@/product/community/access'
import { AttachmentList, type AttachmentInput } from '@/product/community/shared/Attachments'
import { InlineContentComposer } from '@/product/community/shared/InlineContentComposer'

type TopicData = RouterOutput['product']['community']['topics']['getTopic']
type PostData = RouterOutput['product']['community']['topics']['posts']['listPosts']['posts'][number]

type PostProps = {
    post: PostData
    topicLocked: boolean
    canModerate: boolean
    canDelete: boolean
    editingPost: PostData | null
    topic: TopicData | null
    targetPostId: string | null
    updatePost: (post: PostData, input: { message: string; attachments: AttachmentInput }) => Promise<void>
    setEditingPost: (post: PostData | null) => void
    setPostToDelete: (post: PostData) => void
}

export const Post = observer(
    ({
        post,
        topicLocked,
        canModerate,
        canDelete,
        editingPost,
        topic,
        targetPostId,
        updatePost,
        setEditingPost,
        setPostToDelete,
    }: PostProps) => {
        const { i18n, t } = useTranslation()
        const postId = post._id.toString()
        const canEditPost = !topicLocked && (canModerate || isOwnCommunityContent(post.createdBy))
        const canDeletePost = !topicLocked && (canDelete || isOwnCommunityContent(post.createdBy))
        const postCreatedAt = post.createdAt ? formatDateTime(post.createdAt, { i18n }) : ''
        const isEditingPost = editingPost?._id.toString() === postId
        const isNewReply = !!topic?.newRepliesAfter && post.createdAt && post.createdAt > topic.newRepliesAfter

        if (isEditingPost) {
            return (
                <InlineContentComposer
                    initialMessage={post.message}
                    initialAttachments={extractStorageResources(post.attachments)}
                    actor={post.createdBy!}
                    authorDescription={postCreatedAt}
                    messageLabel={t('product.community.topic.postDialog.message')}
                    submitLabel={t('common.save')}
                    cancelLabel={t('common.cancel')}
                    onSubmit={(input) => updatePost(post, input)}
                    onCancel={() => setEditingPost(null)}
                />
            )
        }

        return (
            <Card
                id={`post-${postId}`}
                className={cn(
                    'data-[new-reply=true]:border-primary/50 data-[new-reply=true]:bg-primary/5 scroll-mt-24 gap-0 p-0 transition-colors',
                    {
                        'border-primary bg-primary/5 ring-primary/30 ring-2': targetPostId === postId,
                    }
                )}
                data-new-reply={isNewReply}
            >
                <CardHeader className="gap-3 p-5">
                    <div className="flex items-start gap-4">
                        <Actor size="lg" actor={post.createdBy!} />
                        <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="truncate font-medium">
                                        <ActorName actor={post.createdBy!} />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {postCreatedAt && (
                                            <div className="text-muted-foreground text-sm">{postCreatedAt}</div>
                                        )}
                                        {isNewReply && (
                                            <Badge variant="outline">{t('product.community.topic.post.new')}</Badge>
                                        )}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    {canEditPost && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.community.topic.post.edit')}
                                            onClick={() => setEditingPost(post)}
                                        >
                                            <PencilIcon />
                                        </Button>
                                    )}
                                    {canDeletePost && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.community.topic.post.delete')}
                                            onClick={() => setPostToDelete(post)}
                                        >
                                            <Trash2Icon />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <Markdown className="text-sm leading-relaxed" syntaxHighlighting>
                                    {post.message}
                                </Markdown>
                                <AttachmentList
                                    markdown={post.message}
                                    attachments={extractStorageResources(post.attachments)}
                                />
                            </div>
                        </div>
                    </div>
                </CardHeader>
            </Card>
        )
    }
)
